import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "@volca/ui/lib/toast";
import type {
  ApVendor,
  ApVendorListEdit,
  ApVendorUpdate,
} from "@/accounts-payable/types";
import {
  invalidateQuickbooksBillPushStatuses,
  quickbooksConnectionKey,
} from "@/business/quickbooks-query";
import { useApi } from "@/core/api";
import { vendorQueryOptions } from "./documents";
import { vendorKey, vendorsKey } from "./keys";

type VendorUpdateVariables = {
  businessId: string | null;
  vendorId: string;
} & (
  | { changes: ApVendorUpdate; listEdit?: never }
  | { listEdit: ApVendorListEdit; changes?: never }
);

export const useUpdateVendor = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    scope: { id: "ap-vendor-updates" },
    mutationFn: async ({
      businessId,
      vendorId,
      changes,
      listEdit,
    }: VendorUpdateVariables) => {
      if (listEdit) {
        const vendor = await queryClient.fetchQuery({
          ...vendorQueryOptions(api, businessId, vendorId),
          staleTime: 0,
        });
        const values = vendor[listEdit.field] ?? [];
        const next =
          listEdit.action === "remove"
            ? values.filter((value) => value !== listEdit.value)
            : Array.from(new Set([...values, listEdit.value]));
        changes = { [listEdit.field]: next };
      }
      return api
        .patch<ApVendor>(
          `/businesses/${businessId}/ap/vendors/${vendorId}`,
          changes,
        )
        .then((response) => response.data);
    },
    onSuccess: async (vendor, { businessId, vendorId, changes }) => {
      const key = vendorKey(businessId, vendorId);
      await queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData(key, vendor);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: vendorsKey(businessId) }),
        changes &&
          "quickbooks_vendor_id" in changes &&
          queryClient.invalidateQueries({
            queryKey: quickbooksConnectionKey(businessId),
          }),
        changes &&
          "quickbooks_expense_account_id" in changes &&
          invalidateQuickbooksBillPushStatuses(queryClient, businessId),
      ]);
    },
    onError: (error) => {
      const errors =
        error instanceof AxiosError ? error.response?.data?.errors : undefined;
      toast(
        Array.isArray(errors) && errors.length > 0
          ? `Couldn’t update the vendor: ${errors.join(", ")}`
          : "Couldn’t update the vendor. Please try again.",
      );
    },
  });
};
